import { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { Check, X, Trash2, BadgeCheck } from "lucide-react";
import { api, apiError } from "@/lib/api";
import { Stars } from "@/components/Stars";

const TABS = ["pending", "approved", "rejected"];

export default function Reviews() {
  const qc = useQueryClient();
  const [tab, setTab] = useState("pending");
  const { data: rows = [] } = useQuery({ queryKey: ["admin-reviews"], queryFn: () => api.get("/admin/reviews").then((r) => r.data) });
  const list = rows.filter((r) => r.status === tab);

  const act = async (fn, msg) => {
    try { await fn(); qc.invalidateQueries({ queryKey: ["admin-reviews"] }); qc.invalidateQueries({ queryKey: ["reviews"] }); toast.success(msg); }
    catch (e) { toast.error(apiError(e)); }
  };
  const setStatus = (id, status) => act(() => api.put(`/admin/reviews/${id}`, { status }), `Review ${status}`);
  const remove = (id) => window.confirm("Delete this review permanently?") && act(() => api.delete(`/admin/reviews/${id}`), "Deleted");

  return (
    <div data-testid="admin-reviews">
      <h1 className="font-display text-3xl text-white mb-1">Reviews</h1>
      <p className="text-white/50 text-sm mb-6">Approve client reviews before they appear on Home and Portfolio.</p>
      <div className="flex gap-2 mb-6">
        {TABS.map((t) => (
          <button key={t} onClick={() => setTab(t)} data-testid={`reviews-tab-${t}`}
            className={`px-4 py-2 rounded-full text-xs font-mono-label uppercase tracking-wide ${tab === t ? "btn-gold" : "border border-white/15 text-white/60"}`}>
            {t} ({rows.filter((r) => r.status === t).length})
          </button>
        ))}
      </div>
      {list.length === 0 && <p className="text-white/40 text-sm" data-testid="reviews-empty-admin">No {tab} reviews.</p>}
      <div className="grid md:grid-cols-2 gap-3">
        {list.map((r, i) => (
          <div key={r.id} className="bg-[#111] border border-white/10 rounded-2xl p-5" data-testid={`admin-review-${i}`}>
            <div className="flex items-start justify-between gap-3">
              <div>
                <p className="text-white font-medium text-sm flex items-center gap-1.5">{r.name}{r.verified && <BadgeCheck size={14} className="text-[#D4AF37]" title="Verified paid booking" />}</p>
                <p className="text-white/40 text-xs mt-0.5">{r.service_title || "General"} · {new Date(r.created_at).toLocaleDateString()}</p>
              </div>
              <Stars value={r.rating} size={14} />
            </div>
            <p className="text-white/70 text-sm mt-3 whitespace-pre-line">{r.text}</p>
            <div className="flex gap-2 mt-4">
              {r.status !== "approved" && <button onClick={() => setStatus(r.id, "approved")} data-testid={`approve-review-${i}`} className="flex items-center gap-1 rounded-full px-4 py-1.5 text-xs bg-green-500/15 text-green-400 hover:bg-green-500/25"><Check size={13} /> Approve</button>}
              {r.status !== "rejected" && <button onClick={() => setStatus(r.id, "rejected")} data-testid={`reject-review-${i}`} className="flex items-center gap-1 rounded-full px-4 py-1.5 text-xs bg-white/5 text-white/60 hover:text-white"><X size={13} /> {r.status === "approved" ? "Hide" : "Reject"}</button>}
              <button onClick={() => remove(r.id)} data-testid={`delete-review-${i}`} className="ml-auto text-white/40 hover:text-red-400 p-1.5"><Trash2 size={15} /></button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
